import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, ProgressBar } from "@fluentui/react-components";
import { NewformaButton } from "../_base/Button";
import { useTheme } from "../../hooks/useTheme";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalXL,
        paddingTop: tokens.spacingVerticalXXL,
        textAlign: "left",
        boxSizing: "border-box",
    },
    progressSection: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM,
        width: "100%",
        padding: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalL,
        paddingRight: tokens.spacingHorizontalL,
        borderRadius: tokens.borderRadiusLarge,
        boxSizing: "border-box",
    },
    sendingLabel: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        margin: 0,
    },
    subtext: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        margin: 0,
    },
    fileCount: {
        textAlign: "left",
        fontSize: tokens.fontSizeBase300,
        margin: 0,
    },
    cancelButton: {
        marginTop: tokens.spacingVerticalS,
        alignSelf: "flex-start",
    },
});

interface FileTransferProgressPageProps extends LocalizeContextProps {
    fileUploadCompletedCount: number;
    fileUploadCount: number;
    onCancel: () => void;
    hideCancel?: boolean;
}

const FileTransferProgressPageComponent: React.FC<FileTransferProgressPageProps> = ({
    fileUploadCompletedCount,
    fileUploadCount,
    onCancel,
    hideCancel,
    translate,
}) => {
    const styles = useStyles();
    const { isDarkMode } = useTheme();
    const totalFiles = Math.max(fileUploadCount, 1);
    const currentFileNumber = Math.min(Math.max(fileUploadCompletedCount + 1, 1), totalFiles);
    const displayProgress = Math.min((fileUploadCompletedCount + 0.5) / totalFiles, 0.9);

    return (
        <div className={styles.container} data-testid="file-transfer-progress-page">
            <p className={styles.subtext}>{translate("FLUENT9.FILE_TRANSFER_SUBTEXT") as string}</p>
            <div className={styles.progressSection} style={{ backgroundColor: isDarkMode ? "#3d3d3d" : "#f5f5f5" }}>
                <p className={styles.sendingLabel}>{translate("FLUENT9.FILE_TRANSFER_SENDING_FILES") as string}</p>
                <ProgressBar value={displayProgress} max={1} data-testid="file-transfer-progress-bar" />
                <p className={styles.fileCount} data-testid="file-transfer-progress-count">
                    {translate("FLUENT9.FILE_TRANSFER_UPLOADING") as string} {currentFileNumber}{" "}
                    {translate("FLUENT9.FILE_TRANSFER_OF") as string} {totalFiles}
                </p>
            </div>
            {!hideCancel && (
                <NewformaButton
                    appearance="subtle"
                    className={styles.cancelButton}
                    onClick={onCancel}
                    testid="file-transfer-cancel-button"
                >
                    {translate("FLUENT9.CANCEL_FILE_TRANSFER") as string}
                </NewformaButton>
            )}
        </div>
    );
};

export const FileTransferProgressPage = withLocalize(FileTransferProgressPageComponent);
